import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUIStateContext } from "../../contexts/UIStateContext";
import { useUserContext } from "../../contexts/UserContext";
import { IOption, RowDataReducerAction } from "../../types";
import { getDepartmentNavigationListItems } from "./helpers";
import {
  DEFAULT_OPTION,
  DEFAULT_TOAST,
  FEATURE_FLAG_KEYS,
} from "../LandingPage/constants";
import {
  NavigationMenu,
  IconHome,
  IconOtherReports,
  NavMenuItemsProps,
} from "@cdkglobal/radial";
import { useRefetchTableData } from "../../hooks/navigationQueries";
import { useIntl } from "react-intl";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import { handleSetupSelection } from "../LandingPage/helpers";
import { navigateToHome } from "../../routes/navigation";
import { ampli } from "../../ampli";

export default function SetupSelectorNavMenuItems({
  dispatch,
  handleExpand,
  isCollapsed,
}: Readonly<{
  dispatch: React.Dispatch<RowDataReducerAction>;
  handleExpand: () => void;
  isCollapsed: boolean;
}>): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const enableCAFlow = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_COMMON_AUDIT_FLOW,
    "false",
  );

  const {
    setupListNavInfoByDepartment,
    selected,
    defaultStore,
    setSelected,
    setTableTitleInfo,
    headerLabel,
    iamUserState,
    setupBaseRequestInfo,
    departments,
    evaluatedResults,
    setEvaluatedResults,
    isFetchingUserData,
    permissions,
    setPermissions,
  } = useUserContext();

  const {
    setSearchValue,
    setCurrentPage,
    setPageSizeOption,
    setNetworkErrorToast,
    setActiveTabIndex,
    setSelectedDashboardTabSectionKey,
    setSelectedTabSectionKey,
    setLandingPageToast,
    setIsRecordOpen,
    setIsSideSheetOpen,
    setChipFilterValue,
  } = useUIStateContext();

  const [expandedMenuItemIds, setExpandedMenuItemIds] = useState<
    Record<string, boolean | null | undefined>
  >({});

  useRefetchTableData({
    setupBaseRequestInfo,
    headerLabel,
    landingPageOptions: selected,
    iamUserState,
    evaluatedResults,
    setupListNavInfoByDepartment,
    dispatch,
    setTableTitleInfo,
    setCurrentPage,
    setSearchValue,
    setPageSizeOption,
    setIsRecordOpen,
    setActiveTabIndex,
    setSelectedTabSectionKey,
    setSelectedDashboardTabSectionKey,
    setEvaluatedResults,
    setIsSideSheetOpen,
    setLandingPageToast,
    setSelected,
    setChipFilterValue,
  });

  const handleSetupClick = ({
    setup,
    department,
  }: {
    setup: IOption;
    department: IOption;
  }) => {
    const success = handleSetupSelection({
      setup,
      department,
      store: defaultStore,
      setupListNavInfoByDepartment,
      setLandingPageToast,
      setSelected,
      iamUserState,
      permissions,
      setPermissions,
    });

    if (!success) {
      return;
    }
  };

  const departmentMenuItems = getDepartmentNavigationListItems({
    departments,
    setupListNavInfoByDepartment,
    isFetchingUserData,
    handleSetupClick,
  });

  const navigationMenuItems: NavMenuItemsProps[] = [
    {
      icon: <IconHome />,
      id: "cdcmHome",
      text: "Home",
      tooltip: {
        tooltipProps: {
          id: "setup-selector-navigation-menu-cdcmHome-tooltip",
          text: "Home",
        },
      },
      onClick: () => {
        ampli.selectNavigation({
          "Navigation Item Clicked": true,
          "Navigation Item": "Home",
        });
        setIsRecordOpen(false);
        setNetworkErrorToast(DEFAULT_TOAST);
        setLandingPageToast(DEFAULT_TOAST);

        setSelected((prev) => ({
          ...prev,
          department: DEFAULT_OPTION,
          setup: DEFAULT_OPTION,
        }));

        navigateToHome({
          navigate,
          search: location.search,
        });
      },
    },
    ...departmentMenuItems,
    {
      icon: <IconOtherReports />,
      id: "auditReports",
      tooltip: {
        tooltipProps: {
          id: "setup-selector-navigation-menu-auditReports-tooltip",
          text: intl.formatMessage({
            id: "cdcm.header.auditReport",
            defaultMessage: "Audit Report",
          }),
        },
      },
      text: (
        <b>
          {intl.formatMessage({
            id: "cdcm.header.auditReport",
            defaultMessage: "Audit Report",
          })}
        </b>
      ),
    },
  ];

  const navMenuItems = enableCAFlow
    ? navigationMenuItems
    : navigationMenuItems.filter(
        (item: NavMenuItemsProps) => item.id !== "auditReports",
      );

  return (
    <NavigationMenu
      dataTestId="setup-selector-navigation-menu"
      expandedItemIds={expandedMenuItemIds}
      isCollapsed={isCollapsed}
      onExpand={handleExpand}
      items={navMenuItems}
      onItemsExpandCollapse={setExpandedMenuItemIds}
    />
  );
}
